import { ScrollableWithBackToTopHandle } from '@app/core/ui/ScrollableWithBackToTop';
import { useCallback, useRef } from 'react';

import { useLiveTopicsCategoryFilter } from '../category-toolbar/useLiveTopicsCategoryFilter';
import { useLiveTopicsNewArrivals } from './useLiveTopicsNewArrivals';
import { useLiveTopicsPaginatedData } from './useLiveTopicsPaginatedData';
import { useLiveTopicsScrollLock } from './useLiveTopicsScrollLock';
import { useLiveTopicsSelection } from './useLiveTopicsSelection';
import { useLiveTopicsTextFilter } from './useLiveTopicsTextFilter';

/**
 * Composes the panel-specific state graph into a single hook API.
 * Keeps each concern in its own specialized hook while reducing wiring in the panel component.
 */
export function useLiveTopicsPanelState() {
  const scrollableRef = useRef<ScrollableWithBackToTopHandle>(null);

  const {
    topics,
    isSuccess,
    isLoading,
    isLoadingMore,
    hasMoreOlder,
    loadMoreOlder,
    loadMoreTopicIds,
  } = useLiveTopicsPaginatedData();
  const {
    filteredTopics: categoryFilteredTopics,
    selectedCategories,
    setSelectedCategories,
    categoryOptions,
  } = useLiveTopicsCategoryFilter(topics);
  const { textFilter, setTextFilter, filteredTopics } =
    useLiveTopicsTextFilter(categoryFilteredTopics);
  const { newTopicsCount, setNewTopicsCount } = useLiveTopicsScrollLock();
  const resetNewTopicsCount = useCallback(() => setNewTopicsCount(0), [setNewTopicsCount]);
  const { selectedTopicId, setSelectedTopicId, selectedTopic, isDetailView, handleBack } =
    useLiveTopicsSelection(topics);
  const { newlyArrivedTopicIds, viewedInDetailTopicIds } = useLiveTopicsNewArrivals(
    topics,
    isSuccess,
    loadMoreTopicIds,
    selectedTopicId,
    scrollableRef,
    setNewTopicsCount,
  );

  return {
    topics,
    filters: {
      categoryOptions,
      selectedCategories,
      setSelectedCategories,
      textFilter,
      setTextFilter,
    },
    list: {
      isLoading,
      filteredTopics,
      newlyArrivedTopicIds,
      viewedInDetailTopicIds,
    },
    newTopics: {
      count: newTopicsCount,
      resetCount: resetNewTopicsCount,
    },
    pagination: {
      hasMoreOlder,
      isLoadingMore,
      loadMoreOlder,
      oldestFetchedStartTime: topics[topics.length - 1]?.start_time,
    },
    scroll: {
      scrollableRef,
    },
    selection: {
      selectedTopic,
      setSelectedTopicId,
      isDetailView,
      handleBack,
    },
  };
}
